import { useState } from "react";
import { motion } from "framer-motion";
import { useCommissionMgmt, CommSource, UnifiedCommission, UnifiedPayout } from "@/contexts/CommissionMgmtContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  IndianRupee, Wallet, Users, CheckCircle2, Clock, XCircle,
  ArrowUpCircle, Gift, Briefcase, Megaphone, FileText, Ban,
  CreditCard, TrendingUp, AlertCircle,
} from "lucide-react";

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`p-5 rounded-lg bg-card border border-border shadow-card ${className}`}>{children}</div>
);

const COLORS = {
  accent: "hsl(var(--accent))",
  success: "hsl(142 71% 45%)",
  warning: "hsl(38 92% 50%)",
  destructive: "hsl(0 84% 60%)",
  muted: "hsl(var(--muted-foreground))",
};

const tooltipStyle = {
  contentStyle: { backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px", color: "hsl(var(--foreground))" },
};

const sourceCfg: Record<CommSource, { label: string; color: string; icon: typeof Gift }> = {
  referral: { label: "Referral", color: "bg-accent/10 text-accent border-accent/20", icon: Gift },
  ca_partner: { label: "CA Partner", color: "bg-success/10 text-success border-success/20", icon: Briefcase },
  affiliate: { label: "Affiliate", color: "bg-warning/10 text-warning border-warning/20", icon: Megaphone },
};

const commStatusCfg = {
  pending: { label: "Pending", color: "bg-muted text-muted-foreground", icon: Clock },
  approved: { label: "Approved", color: "bg-accent/10 text-accent border-accent/20", icon: CheckCircle2 },
  paid: { label: "Paid", color: "bg-success/10 text-success border-success/20", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-destructive/10 text-destructive border-destructive/20", icon: XCircle },
};

const payoutStatusCfg = {
  requested: { label: "Requested", color: "bg-warning/10 text-warning border-warning/20", icon: Clock },
  processing: { label: "Processing", color: "bg-accent/10 text-accent border-accent/20", icon: ArrowUpCircle },
  completed: { label: "Completed", color: "bg-success/10 text-success border-success/20", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-destructive/10 text-destructive border-destructive/20", icon: XCircle },
};

export default function CommissionManagementPage() {
  const { commissions, payouts, logs, summary, approveCommission, rejectCommission, approvePayout, rejectPayout, completePayout } = useCommissionMgmt();
  const [sourceFilter, setSourceFilter] = useState<CommSource | "all">("all");
  const [completeRef, setCompleteRef] = useState<Record<string, string>>({});

  const filtered = sourceFilter === "all" ? commissions : commissions.filter(c => c.source === sourceFilter);
  const filteredPayouts = sourceFilter === "all" ? payouts : payouts.filter(p => p.source === sourceFilter);

  // Chart data
  const monthlyBySource = (() => {
    const months: Record<string, Record<string, number>> = {};
    commissions.forEach(c => {
      if (!months[c.month]) months[c.month] = { referral: 0, ca_partner: 0, affiliate: 0 };
      months[c.month][c.source] += c.amount;
    });
    return Object.entries(months).sort(([a], [b]) => a.localeCompare(b)).map(([m, v]) => ({
      month: new Date(m + "-01").toLocaleDateString("en-IN", { month: "short", year: "2-digit" }),
      Referral: v.referral, "CA Partner": v.ca_partner, Affiliate: v.affiliate,
    }));
  })();

  const sourceBreakdown = [
    { name: "Referral", value: summary.referralCount, color: COLORS.accent },
    { name: "CA Partner", value: summary.partnerCount, color: COLORS.success },
    { name: "Affiliate", value: summary.affiliateCount, color: COLORS.warning },
  ].filter(d => d.value > 0);

  const handleApproveComm = (id: string) => { approveCommission(id); toast.success("Commission approved"); };
  const handleRejectComm = (id: string) => { rejectCommission(id); toast.success("Commission rejected"); };
  const handleApprovePayout = (id: string) => { approvePayout(id); toast.success("Payout approved — processing"); };
  const handleRejectPayout = (id: string) => { rejectPayout(id); toast.success("Payout rejected"); };
  const handleCompletePayout = (id: string) => {
    const ref = completeRef[id] || `TXN-${Date.now().toString(36).toUpperCase()}`;
    completePayout(id, ref);
    toast.success("Payout marked as completed");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">Commission Management</h1>
        <p className="text-sm text-muted-foreground">Track and manage commissions across referrals, CA partners, and affiliates</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {[
          { label: "Total Commissions", value: `₹${summary.totalCommissions}`, icon: IndianRupee, color: "text-accent" },
          { label: "Pending Approval", value: `₹${summary.totalPending}`, icon: Clock, color: "text-warning" },
          { label: "Approved", value: `₹${summary.totalApproved}`, icon: CheckCircle2, color: "text-accent" },
          { label: "Paid Out", value: `₹${summary.totalPaid}`, icon: CreditCard, color: "text-success" },
          { label: "Payout Requests", value: `₹${summary.totalPayoutRequested}`, icon: AlertCircle, color: "text-warning" },
          { label: "Payouts Completed", value: `₹${summary.totalPayoutCompleted}`, icon: TrendingUp, color: "text-success" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Card>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] text-muted-foreground">{s.label}</span>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <div className="text-xl font-bold font-display text-foreground">{s.value}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Monthly Commissions by Source</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={monthlyBySource} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${v}`} />
              <Tooltip {...tooltipStyle} formatter={(v: number) => `₹${v}`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Referral" fill={COLORS.accent} stackId="a" />
              <Bar dataKey="CA Partner" fill={COLORS.success} stackId="a" />
              <Bar dataKey="Affiliate" fill={COLORS.warning} stackId="a" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card>
          <h3 className="text-sm font-semibold text-foreground mb-4">Commission Distribution</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={sourceBreakdown} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={4} strokeWidth={0}>
                {sourceBreakdown.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Source Filter */}
      <div className="flex gap-2 flex-wrap">
        {(["all", "referral", "ca_partner", "affiliate"] as const).map(s => (
          <Button key={s} variant={sourceFilter === s ? "default" : "outline"} size="sm" onClick={() => setSourceFilter(s)}
            className="text-xs capitalize">
            {s === "all" ? "All Sources" : s === "ca_partner" ? "CA Partner" : s}
          </Button>
        ))}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="commissions">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="commissions">Commissions</TabsTrigger>
          <TabsTrigger value="pending-payouts">Pending Payouts</TabsTrigger>
          <TabsTrigger value="all-payouts">All Payouts</TabsTrigger>
          <TabsTrigger value="logs">Payment Logs</TabsTrigger>
        </TabsList>

        {/* Commission History */}
        <TabsContent value="commissions">
          <Card>
            <div className="space-y-2 max-h-[450px] overflow-y-auto pr-1">
              {filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(cm => {
                const src = sourceCfg[cm.source];
                const SrcIcon = src.icon;
                const st = commStatusCfg[cm.status];
                const StIcon = st.icon;
                return (
                  <div key={cm.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50 gap-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <SrcIcon className="h-4 w-4 text-accent" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{cm.description}</p>
                        <p className="text-xs text-muted-foreground">{cm.userName} · {cm.month}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {cm.status === "pending" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => handleApproveComm(cm.id)} className="text-xs h-7 px-2 text-success border-success/30 hover:bg-success/10">
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleRejectComm(cm.id)} className="text-xs h-7 px-2 text-destructive border-destructive/30 hover:bg-destructive/10">
                            <Ban className="h-3 w-3 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      <span className="text-sm font-bold text-foreground">₹{cm.amount}</span>
                      <Badge variant="outline" className={`text-[10px] ${src.color}`}>{src.label}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${st.color} flex items-center gap-1`}>
                        <StIcon className="h-2.5 w-2.5" /> {st.label}
                      </Badge>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No commissions found</p>}
            </div>
          </Card>
        </TabsContent>

        {/* Pending Payouts */}
        <TabsContent value="pending-payouts">
          <Card>
            <div className="space-y-3 max-h-[450px] overflow-y-auto pr-1">
              {filteredPayouts.filter(p => p.status === "requested" || p.status === "processing")
                .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime())
                .map(po => {
                  const src = sourceCfg[po.source];
                  const st = payoutStatusCfg[po.status];
                  const StIcon = st.icon;
                  return (
                    <div key={po.id} className="p-4 rounded-lg bg-muted/20 border border-border/50 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">{po.userName} — ₹{po.amount}</p>
                          <p className="text-xs text-muted-foreground">
                            {po.method} · Requested {new Date(po.requestedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-[10px] ${src.color}`}>{src.label}</Badge>
                          <Badge variant="outline" className={`text-[10px] ${st.color} flex items-center gap-1`}>
                            <StIcon className="h-2.5 w-2.5" /> {st.label}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {po.status === "requested" && (
                          <>
                            <Button size="sm" onClick={() => handleApprovePayout(po.id)} className="text-xs h-7 gap-1">
                              <CheckCircle2 className="h-3 w-3" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleRejectPayout(po.id)} className="text-xs h-7 gap-1 text-destructive">
                              <XCircle className="h-3 w-3" /> Reject
                            </Button>
                          </>
                        )}
                        {po.status === "processing" && (
                          <>
                            <Input
                              value={completeRef[po.id] || ""}
                              onChange={e => setCompleteRef(prev => ({ ...prev, [po.id]: e.target.value }))}
                              placeholder="Transaction ref (optional)"
                              className="flex-1 h-7 text-xs"
                            />
                            <Button size="sm" onClick={() => handleCompletePayout(po.id)} className="text-xs h-7 gap-1">
                              <CheckCircle2 className="h-3 w-3" /> Complete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              {filteredPayouts.filter(p => p.status === "requested" || p.status === "processing").length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">No pending payouts</p>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* All Payouts */}
        <TabsContent value="all-payouts">
          <Card>
            <div className="space-y-2 max-h-[450px] overflow-y-auto pr-1">
              {filteredPayouts.sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()).map(po => {
                const src = sourceCfg[po.source];
                const st = payoutStatusCfg[po.status];
                const StIcon = st.icon;
                return (
                  <div key={po.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{po.userName} — ₹{po.amount}</p>
                      <p className="text-xs text-muted-foreground">
                        {po.method} · {new Date(po.requestedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        {po.transactionRef && ` · ${po.transactionRef}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Badge variant="outline" className={`text-[10px] ${src.color}`}>{src.label}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${st.color} flex items-center gap-1`}>
                        <StIcon className="h-2.5 w-2.5" /> {st.label}
                      </Badge>
                    </div>
                  </div>
                );
              })}
              {filteredPayouts.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No payouts found</p>}
            </div>
          </Card>
        </TabsContent>

        {/* Payment Logs */}
        <TabsContent value="logs">
          <Card>
            <div className="space-y-2 max-h-[450px] overflow-y-auto pr-1">
              {logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).map(log => {
                const actionCfg: Record<string, { color: string; icon: typeof CheckCircle2 }> = {
                  requested: { color: "text-muted-foreground", icon: Clock },
                  approved: { color: "text-accent", icon: CheckCircle2 },
                  rejected: { color: "text-destructive", icon: XCircle },
                  processing: { color: "text-warning", icon: ArrowUpCircle },
                  completed: { color: "text-success", icon: CheckCircle2 },
                };
                const cfg = actionCfg[log.action] || actionCfg.requested;
                const LogIcon = cfg.icon;
                return (
                  <div key={log.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border/50">
                    <LogIcon className={`h-4 w-4 ${cfg.color} flex-shrink-0`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground">
                        <span className="font-medium capitalize">{log.action}</span> by {log.performedBy}
                        {log.note && <span className="text-muted-foreground"> — {log.note}</span>}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        Payout {log.payoutId} · {new Date(log.timestamp).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })}
              {logs.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No payment logs</p>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
